/**
 * A creative's chips under its name (spec §3.3): Top, Fatigue and n<500 (§4.3), then its status (§4.4, R11). The
 * labels come from the view, computed before any filter; `unknown` status draws no chip. Each chip is a word in a
 * bordered pill, so none relies on colour alone, and each stays upright in a faded (italic) row.
 *
 * @module components/creatives/CreativeChips
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { campaignStateText, type TFn } from '@/services/channels/channelFormat';
import { fatigueChipText, topChipText } from '@/services/creatives/creativeText';
import type { CreativeRow } from '@/services/creatives/creativeTypes';

const CHIP = 'inline-block whitespace-nowrap rounded-full border px-1.5 text-[11px] not-italic print:whitespace-normal';
const TOP_CLASS = `${CHIP} border-success-700 text-success-700`;
const FATIGUE_CLASS = `${CHIP} border-warning-700 text-warning-700`;
const PLAIN_CLASS = `${CHIP} border-neutral-400 text-neutral-800`;

interface ChipSpec {
  id: 'top' | 'fatigue' | 'lowSample' | 'state';
  text: string;
  className: string;
  title?: string;
}

const chipsOf = (row: CreativeRow, t: TFn): ChipSpec[] => [
  ...(row.top ? [{ id: 'top' as const, text: topChipText(row, t), className: TOP_CLASS }] : []),
  ...(row.fatigueDrop === null ? [] : [{ id: 'fatigue' as const, text: fatigueChipText(row.fatigueDrop, t), className: FATIGUE_CLASS }]),
  ...(row.lowSample ? [{ id: 'lowSample' as const, text: t('overview.lowSample'), className: PLAIN_CLASS, title: t('overview.lowSampleTitle') }] : []),
  ...(row.state.kind === 'unknown' ? [] : [{ id: 'state' as const, text: campaignStateText(row.state, t), className: PLAIN_CLASS }]),
];

export const CreativeChips: React.FC<{ row: CreativeRow }> = ({ row }) => {
  const { t } = useTranslation();
  const chips = chipsOf(row, t);
  if (chips.length === 0) return null;
  return (
    <span data-creative-chips className="mt-1 flex flex-wrap items-center gap-1">
      {chips.map((c) => <span key={c.id} data-chip={c.id} title={c.title} className={c.className}>{c.text}</span>)}
    </span>
  );
};
